import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function CannedResponses() {
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({ title: "", body: "", shortcut: "" });
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ field: "title", dir: "asc" });

    const filtered = useMemo(() => {
        const base = rows.filter((c) => !q || `${c.title} ${c.shortcut || ""} ${c.body}`.toLowerCase().includes(q.toLowerCase()));
        return sortRows(base, sort);
    }, [rows, q, sort]);

    const load = async () => {
        try { const { data } = await api.get("/canned-responses"); setRows(data); }
        catch (err) { setError(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm({ title: "", body: "", shortcut: "" }); setModal({ mode: "create" }); };
    const openEdit = (c) => { setForm({ title: c.title, body: c.body, shortcut: c.shortcut || "" }); setModal({ mode: "edit", canned: c }); };

    const save = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            if (modal.mode === "create") await api.post("/canned-responses", form);
            else await api.patch(`/canned-responses/${modal.canned.canned_id}`, form);
            setModal(null); await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar plantilla "${c.title}"?`)) return;
        try { await api.delete(`/canned-responses/${c.canned_id}`); await load(); }
        catch (err) { setError(formatApiError(err)); }
    };

    return (
        <div data-testid="canned-page">
            <div className="page-head">
                <div>
                    <h1>Respuestas plantilla</h1>
                    <p>Guarda respuestas frecuentes para insertarlas en un click al responder tickets.</p>
                </div>
                <button className="btn btn-primary" onClick={openCreate} data-testid="canned-create-btn"><Plus size={16} /> Nueva plantilla</button>
            </div>

            {error && <div className="alert alert-error" data-testid="canned-error">{error}</div>}

            <div className="card">
                <div className="card-header">
                    <h3>Listado ({filtered.length} de {rows.length})</h3>
                    <div className="tbar"><input className="input" style={{ width: 280 }} placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="canned-search" /></div>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead><tr><SortableTh field="title" sort={sort} onChange={setSort}>Título</SortableTh><SortableTh field="shortcut" sort={sort} onChange={setSort}>Shortcut</SortableTh><SortableTh field="body" sort={sort} onChange={setSort}>Vista previa</SortableTh><th style={{ textAlign: "right", padding: "10px 12px", color: "#A3A3A8", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: "1px solid #26262B" }}>Acciones</th></tr></thead>
                        <tbody>
                            {filtered.length === 0 && <tr><td colSpan={4} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin plantillas.</td></tr>}
                            {filtered.map((c) => (
                                <tr key={c.canned_id} data-testid={`canned-row-${c.canned_id}`}>
                                    <td><b>{c.title}</b></td>
                                    <td className="mono text-sm text-muted">{c.shortcut ? `/${c.shortcut}` : "—"}</td>
                                    <td className="text-muted text-sm" style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.body}</td>
                                    <td>
                                        <div className="actions">
                                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)} data-testid={`canned-edit-${c.canned_id}`}><Pencil size={13} /> Editar</button>
                                            <button className="btn btn-danger btn-sm" onClick={() => remove(c)} data-testid={`canned-delete-${c.canned_id}`}><Trash2 size={13} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={!!modal}
                onClose={() => setModal(null)}
                title={modal?.mode === "create" ? "Nueva plantilla" : "Editar plantilla"}
                testId="canned-modal"
                footer={<>
                    <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
                    <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="canned-modal-save">{saving ? <span className="loader" /> : "Guardar"}</button>
                </>}
            >
                <form onSubmit={save} className="row" style={{ gap: 14 }}>
                    <div className="field">
                        <label>Título *</label>
                        <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="canned-modal-title" />
                    </div>
                    <div className="field">
                        <label>Shortcut (opcional, sin /)</label>
                        <input className="input" value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value })} placeholder="ej. saludo" data-testid="canned-modal-shortcut" />
                    </div>
                    <div className="field">
                        <label>Contenido *</label>
                        <textarea className="textarea" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="canned-modal-body" />
                    </div>
                </form>
            </Modal>
        </div>
    );
}
